
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Printer, FileText, Loader2, Receipt } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { printCashierClosingReport } from '@/services/printService';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface CashierClosingReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cashierData: any;
  salesData: any;
  paymentsData: any;
}

const CashierClosingReportDialog: React.FC<CashierClosingReportDialogProps> = ({
  open,
  onOpenChange,
  cashierData,
  salesData,
  paymentsData
}) => {
  const { toast } = useToast();
  const [isPrinting, setIsPrinting] = useState(false);
  const [useThermalFormat, setUseThermalFormat] = useState(() => 
    localStorage.getItem('use_thermal_receipt_format') === 'true'
  );
  
  const handleSwitchChange = (checked: boolean) => {
    setUseThermalFormat(checked);
    localStorage.setItem('use_thermal_receipt_format', checked ? 'true' : 'false');
  };
  
  const handlePrint = async () => {
    setIsPrinting(true);
    
    try {
      await printCashierClosingReport(cashierData, salesData, paymentsData);
      toast({
        title: "Relatório enviado para impressão",
        description: "O relatório de fechamento foi enviado para a impressora.",
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Erro ao imprimir:", error);
      toast({
        title: "Erro ao imprimir",
        description: error instanceof Error ? error.message : "Ocorreu um erro ao enviar para a impressora.",
        variant: "destructive"
      });
    } finally {
      setIsPrinting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Relatório de Fechamento de Caixa
          </DialogTitle>
          <DialogDescription>
            Visualize o resumo do fechamento de caixa e imprima o relatório
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Data:</p>
                <p className="font-medium">{new Date().toLocaleDateString('pt-BR')}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Operador:</p>
                <p className="font-medium">{cashierData.operator || 'Não informado'}</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-md font-semibold border-b pb-1 mb-2">Resumo de Vendas</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total de Pedidos:</span>
                  <span>{salesData.totalOrders || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor Total:</span>
                  <span className="font-medium">{formatCurrency(salesData.totalAmount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ticket Médio:</span>
                  <span>{formatCurrency(salesData.averageTicket || 0)}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-md font-semibold border-b pb-1 mb-2">Formas de Pagamento</h3>
              <div className="space-y-2">
                {paymentsData.length > 0 ? (
                  paymentsData.map((payment: any, index: number) => (
                    <div key={index} className="flex justify-between text-sm border-b border-dashed border-gray-200 pb-1">
                      <span>{payment.method ? payment.method.name : payment.name}</span>
                      <div className="flex gap-4">
                        <span className="text-muted-foreground w-8 text-center">{payment.count}x</span>
                        <span className="font-medium">{formatCurrency(payment.total || payment.amount)}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">Nenhum pagamento registrado.</p>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="text-md font-semibold border-b pb-1 mb-2">Resumo do Caixa</h3>
              <div className="space-y-1 text-sm">
                {cashierData.initialAmount !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Saldo Inicial:</span>
                    <span>{formatCurrency(cashierData.initialAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entradas:</span>
                  <span>{formatCurrency(cashierData.totalIn || salesData.totalAmount || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Saídas:</span>
                  <span>{formatCurrency(cashierData.totalOut || 0)}</span>
                </div>
                <div className="flex justify-between font-medium border-t mt-1 pt-1">
                  <span>Saldo Final (Esperado):</span>
                  <span>{formatCurrency(cashierData.expectedAmount || salesData.totalAmount || 0)}</span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        
        <div className="flex items-center space-x-2 pt-4 border-t">
          <Switch
            id="thermal-format"
            checked={useThermalFormat}
            onCheckedChange={handleSwitchChange}
          />
          <Label htmlFor="thermal-format" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Formato de cupom térmico
          </Label>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button 
            onClick={handlePrint} 
            disabled={isPrinting}
            className="flex items-center gap-2"
          >
            {isPrinting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Imprimindo...</span>
              </>
            ) : (
              <>
                <Printer className="h-4 w-4" />
                <span>Imprimir Relatório</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CashierClosingReportDialog;
